import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
  HTMLAttributes,
} from 'react';
import 'intersection-observer';

type LazyProps = {
  caffeinate?: boolean;
  children?: ReactNode;
} & HTMLAttributes<HTMLDivElement>;

const Lazy: FunctionComponent<LazyProps> = ({ caffeinate = true, children, ...props }) => {
  const [observeRef, intersecting] = useIntersectionObserver(caffeinate);
  return (
    <div {...props} ref={observeRef}>
      {intersecting ? children : null}
    </div>
  );
};
export default Lazy;

function useIntersectionObserver(caffeinate: boolean) {
  const rootMargin = '0px 0px 500px 0px';
  const [intersecting, setIntersecting] = useState(false);
  const [node, setNode] = useState<HTMLDivElement | undefined>(undefined);

  // Use a ref callback instead of useRef to make the component render again when ref is available
  const observeRef = useCallback((ref: HTMLDivElement) => {
    if (ref) setNode(ref);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency management
  useEffect(() => {
    // Nothing to observe yet
    if (!node) return undefined;

    // Prevent the intersecting component to become lazy again if caffeinate is true
    if (caffeinate && intersecting) return undefined;

    const observer = new IntersectionObserver(
      entries => entries.forEach(({ isIntersecting }) => setIntersecting(isIntersecting)),
      { rootMargin }
    );

    observer.observe(node);
    return () => observer.unobserve(node);
  }, [caffeinate, intersecting, node, rootMargin]);

  return [observeRef, intersecting] as const;
}
